// Block-2-Karte „An- & Abwesenheiten" (AP-A, DD10): listet ALLE Mitarbeiter
// (nach Vorname sortiert) mit ihrem heutigen Präsenz-Status — anwesend (grün)
// oder Abwesenheits-Typ (Urlaub/Homeoffice/Krank/Abwesend). Jeder Eintrag zeigt
// einen Avatar-Kreis mit Kürzel, überlagert vom Entra-Profilfoto, falls vorhanden.
//
// Datenquelle: `dashboardService.getAbsencesToday()` → `GET /dashboard/absences/today`
// (Tages-Cache aus dem Outlook-/Graph-Kalender, Organisator-basierte Zuordnung).
import * as React from "react";
import { useEffect, useState } from "react";
import { PersonAvailable20Regular } from "@fluentui/react-icons";
import {
    dashboardService,
    type PersonPresence,
    type PresenceStatus,
} from "../../services/dashboardService";
import { useBlobUrl } from "../../hooks/useBlobUrl";
import { REFINED } from "../risikoanalyseStep/tokens";
import { DashCard, EmptyHint, deDate } from "./DashboardShared";

/** Anzeige-Label + Farbe je Status (present = grün; K4-konform). */
const STATUS_STYLE: Record<PresenceStatus, { label: string; bg: string; fg: string }> = {
    present: { label: "Anwesend", bg: "#e7f3ec", fg: REFINED.ok },
    urlaub: { label: "Urlaub", bg: "#fdf4e0", fg: "#8a6d00" },
    homeoffice: { label: "Homeoffice", bg: REFINED.aub50, fg: REFINED.aub },
    krank: { label: "Krank", bg: REFINED.errBg, fg: REFINED.err },
    abwesend: { label: "Abwesend", bg: "#eee", fg: REFINED.ink3 },
};

export const AnAbwesenheitenCard: React.FC = () => {
    const [people, setPeople] = useState<PersonPresence[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        dashboardService
            .getAbsencesToday()
            .then((res) => {
                if (cancelled) return;
                // Nach Vorname sortieren (Backend liefert bereits so; defensiv).
                const sorted = [...res.people].sort((a, b) =>
                    (a.firstName ?? a.name).localeCompare(b.firstName ?? b.name, "de", {
                        sensitivity: "base",
                    }),
                );
                setPeople(sorted);
            })
            .catch(() => {
                if (!cancelled) setPeople([]);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const present = people.filter((p) => p.status === "present").length;
    const summary = loading ? null : `${present} von ${people.length} anwesend`;

    return (
        <DashCard
            title="An- & Abwesenheiten"
            icon={<PersonAvailable20Regular />}
            right={
                summary ? (
                    <span style={{ fontSize: "0.82rem", fontWeight: 700, color: REFINED.ok }}>
                        {summary}
                    </span>
                ) : undefined
            }
        >
            {loading ? (
                <EmptyHint>Lade Anwesenheiten…</EmptyHint>
            ) : people.length === 0 ? (
                <EmptyHint>Keine Mitarbeiterdaten verfügbar.</EmptyHint>
            ) : (
                <div style={{ display: "flex", flexDirection: "column", padding: "2px 0" }}>
                    {people.map((p) => (
                        <PresenceRow key={p.userId ?? `${p.kuerzel}-${p.name}`} person={p} />
                    ))}
                </div>
            )}
        </DashCard>
    );
};

/* ────────── Eine Mitarbeiter-Zeile: Avatar + Name + Status-Badge ────────── */

const PresenceRow: React.FC<{ person: PersonPresence }> = ({ person }) => {
    const s = STATUS_STYLE[person.status] ?? STATUS_STYLE.abwesend;
    const absent = person.status !== "present";
    return (
        <div
            style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "5px 2px",
                borderBottom: `1px solid ${REFINED.line}`,
            }}
            title={`${person.name} · ${s.label}${
                absent && person.until ? ` (bis ${deDate(person.until)})` : ""
            }`}
        >
            <PresenceAvatar userId={person.userId} kuerzel={person.kuerzel} name={person.name} />
            <span
                style={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: "0.88rem",
                    fontWeight: 600,
                    color: REFINED.ink,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                }}
            >
                {person.name}
            </span>
            {absent && person.until && (
                <span style={{ fontSize: "0.72rem", color: REFINED.ink3, fontVariantNumeric: "tabular-nums" }}>
                    bis {deDate(person.until)}
                </span>
            )}
            <span
                style={{
                    flexShrink: 0,
                    padding: "2px 9px",
                    borderRadius: 999,
                    fontSize: "0.72rem",
                    fontWeight: 800,
                    backgroundColor: s.bg,
                    color: s.fg,
                    whiteSpace: "nowrap",
                }}
            >
                {s.label}
            </span>
        </div>
    );
};

/* ────────── Avatar-Kreis: Kürzel, überlagert vom Profilfoto falls vorhanden ────────── */

const PresenceAvatar: React.FC<{
    userId: string | null;
    kuerzel: string | null;
    name: string;
    size?: number;
}> = ({ userId, kuerzel, name, size = 30 }) => {
    const ref = userId ? `/users/${encodeURIComponent(userId)}/photo` : null;
    const { url } = useBlobUrl(ref);
    const initials = (kuerzel?.trim() || name.trim().slice(0, 2) || "?").toUpperCase().slice(0, 3);
    return (
        <span
            aria-hidden
            style={{
                width: size,
                height: size,
                flexShrink: 0,
                borderRadius: "50%",
                overflow: "hidden",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#ffa714",
                color: "#361836",
                fontWeight: 800,
                fontSize: Math.round(size * 0.36),
                lineHeight: 1,
            }}
        >
            {url ? (
                <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            ) : (
                initials
            )}
        </span>
    );
};

export default AnAbwesenheitenCard;
